/**
 * Shared filter constants and factory for ChartsEmbedder datasets.
 *
 * Centralizes the definition of common filters (installation, region, sector, etc.)
 * so individual dataset modules only need to import and override what's different.
 *
 * Usage in a dataset module:
 *   import { buildCommonFilters, embedFilter } from '../sharedFilters'
 *   const commonFilters = buildCommonFilters('my_dataset', {
 *     INSTALLATION: { label: 'REMS' },
 *     STATUS: { column: 'my_status_table.name' },
 *     TENANT: false, // explicitly exclude
 *   })
 */

/**
 * Standard mapItem function for embed filters — extracts the first value
 * from the streamed JSON object and uses it as both value and label.
 */
export const defaultMapItem = item => {
  const v = Object.values(item)[0]
  return { value: v, label: v }
}

/**
 * mapItem for embed filters over a NUMERIC column.
 *
 * Same contract as defaultMapItem — the value is whatever the dataset streams,
 * nothing is hardcoded — but cast to a string. The panel round-trips a selection
 * as `values.join(',')` and reads it back with `String(val).split(',')`, while
 * vue-select matches an option to its reduced value with `JSON.stringify`. With a
 * numeric option value the two never match again after the first click, so the
 * dropdown stops marking it as selected and re-clicking appends a duplicate
 * instead of deselecting. profileMapItem casts for the same reason.
 */
export const numericMapItem = item => {
  const raw = item.filter !== undefined ? item.filter : Object.values(item)[0]
  const value = raw === null || raw === undefined ? null : String(raw)
  return { value, label: value }
}

/**
 * mapItem factory for embed filters over a coded column (0/1 flags, small
 * enums): the value stays whatever the dataset streams (cast like
 * numericMapItem), only the label is resolved through `labels`, falling back
 * to the raw value. Nothing is hardcoded: a code the dataset never returns is
 * never offered.
 */
export const labeledMapItem = labels => item => {
  const raw = item.filter !== undefined ? item.filter : Object.values(item)[0]
  const value = raw === null || raw === undefined ? null : String(raw)
  return { value, label: labels[value] || value }
}

/**
 * Helper to create a single embed-type filter.
 * Reduces boilerplate in dataset config files.
 *
 * @param {string} dataset     - dataset key (e.g. 'inventories')
 * @param {string} column      - database column to filter on (e.g. 'units.description')
 * @param {string} label       - human-readable label
 * @param {string} key         - URL param key (e.g. 'installation')
 * @param {Object} [extra={}]  - extra overrides for the filter object
 * @returns {Object} a complete filter definition
 */
export function embedFilter(dataset, column, label, key, extra = {}) {
  const { optionsOverrides, ...filterOverrides } = extra
  return {
    key,
    label,
    filterType: 'where',
    whereColumn: column,
    optionsConfig: {
      type: 'embed',
      dataset,
      by: column,
      mapItem: defaultMapItem,
      limit: -1,
      valueField: 'value',
      ...optionsOverrides,
    },
    ...filterOverrides,
  }
}

/**
 * Special case for the Usuario filter.
 *
 * Renders a composite dropdown label (users.user + users.name + users.first_name
 * + users.second_name) so operators identify users by full name, while resolving
 * the selection against the numeric primary key (users.id) — username changes do
 * not invalidate stored filters.
 *
 * Filter behavior:
 *   - `filterType: 'where'` + `whereColumn: 'users.id'` → emits
 *     `where={"users.id":{"operator":"in","values":["<id>"]}}` and participates
 *     in buildCascadeWhere automatically (user selection narrows other filters).
 *   - `optionsConfig.type: 'user-embed'` is handled by useChartFilterOptions.js,
 *     which fetches the composite columns and maps rows to
 *     `{ value: users.id, label: composite }`.
 *
 * @param {string} dataset     - dataset key (e.g. 'plannings')
 * @param {string} [label]     - visible label (default 'Usuario')
 * @returns {Object} filter definition
 */
export function userFilter(dataset, label = 'Usuario') {
  return {
    key: 'id_user',
    label,
    filterType: 'where',
    whereColumn: 'users.id',
    optionsConfig: {
      type: 'user-embed',
      dataset,
      valueField: 'value',
    },
  }
}

/**
 * Profile options provided by the user.
 */
export const PROFILE_OPTIONS = [
  { value: '86', label: 'Administrador de seguridad operativa' },
  { value: '95', label: 'Administración de Inventario Teórico' },
  { value: '105', label: 'Carga de perfiles administración_inventario y usuario_general.' },
  { value: '89', label: 'Analista Soporte Proyectos' },
  { value: '98', label: 'Checker Finanzas' },
  { value: '103', label: 'Director Nacional Banamex' },
  { value: '67', label: 'Directo Regional Banamex' },
  { value: '101', label: 'Fire Safety Offices' },
  { value: '87', label: 'Generic_Super_Banamex' },
  { value: '76', label: 'Gerente Banamex' },
  { value: '10', label: 'All permissions' },
  { value: '79', label: 'Operador de Monitoreo' },
  { value: '88', label: 'Operador Soporte Técnico BAU' },
  { value: '100', label: 'Operador de Videovigilancia' },
  { value: '73', label: 'Program Manager Banamex' },
  { value: '102', label: 'Risk & Control Banamex' },
  { value: '70', label: 'Safety Officer Banamex' },
  { value: '104', label: 'Security Banamex' },
  { value: '61', label: 'Soporte administrativo Banamex' },
  { value: '94', label: 'Soporte Seguridad Control y Acceso' },
  { value: '64', label: 'Subdirector Banamex' },
  { value: '92', label: 'Superusuario Monitoreo_Soporte' },
  { value: '4', label: 'Service Provider' },
  { value: '106', label: 'Combinación de perfiles usuario_inventario y checker_finanzas' },
  { value: '82', label: 'Supervisor monitoreo' },
  { value: '91', label: 'Supervisor Soporte Proyectos' },
  { value: '90', label: 'Supervisor Soporte Técnico' },
  { value: '93', label: 'Read-Only: User may view Seproban image folders...' },
  { value: '96', label: 'Administración de Inventario Teórico' },
  { value: '99', label: 'Usuario Regional' },
]

/**
 * Profile mapping for translating IDs into descriptions.
 */
export const PROFILE_MAPPING = PROFILE_OPTIONS.reduce((acc, opt) => {
  acc[opt.value] = opt.label
  return acc
}, {})

/**
 * mapItem for profile embed filters — extracts the profile ID from the streamed
 * item and resolves it to its human-readable label via PROFILE_MAPPING.
 */
export const profileMapItem = item => {
  const v = item.filter !== undefined ? item.filter : Object.values(item)[0]
  const strV = v != null ? String(v) : null
  return { value: strV, label: PROFILE_MAPPING[strV] || strV }
}

/**
 * Canonical filter key names used across all datasets.
 */
export const SHARED_FILTER_KEYS = {
  INSTALLATION: 'installation',
  REGION: 'region',
  MUNICIPALITY: 'municipality',
  SECTOR: 'sector',
  CENTER: 'center',
  TENANT: 'tenant',
  STATUS: 'status',
  USER: 'user',
  PROFILE: 'profile',
  REFERENCE: 'reference',
  TYPE: 'type',
  NAME: 'name',
  GROUP: 'group',
  LEVEL: 'level',
}

/**
 * Common filter definitions — the "canonical" set.
 * Each dataset can import the ones it needs and override label/column.
 */
const COMMON_DEFINITIONS = {
  INSTALLATION: { key: 'installation', label: 'Inmuebles', column: 'units.description' },

  REGION: { key: 'region', label: 'Estado', column: 'regions.name' },
  MUNICIPALITY: { key: 'municipality', label: 'Municipio', column: 'municipalities.name' },

  SECTOR: { key: 'sector', label: 'Region', column: 'sectors.name' },
  CENTER: { key: 'center', label: 'Centro', column: 'centers.name' },

  TENANT: { key: 'tenant', label: 'Tenant', column: 'tenants.name' },
  STATUS: { key: 'status', label: 'Estado', column: null }, // varies per dataset
  USER: { key: 'user', label: 'Usuario', column: 'users.user' },
  PROFILE: {
    key: 'profile',
    label: 'Perfil',
    column: 'users.id_profile',
    filterType: 'where',
    optionsConfig: {
      type: 'static',
      options: PROFILE_OPTIONS,
    },
  },
}

/**
 * Build the common filters for a dataset.
 *
 * @param {string} datasetKey   - dataset identifier (e.g. 'inventories')
 * @param {Object} [overrides]  - per-constant overrides.
 *   - Pass `{ label, column }` to override defaults
 *   - Pass `false` to exclude that filter entirely
 *   - Pass extra keys like `filterType`, `optionsOverrides` to customise further
 * @returns {Array} filter definitions ready for the module's `filters` array
 *
 * @example
 * buildCommonFilters('inventories', {
 *   INSTALLATION: { label: 'REMS' },
 *   STATUS: { column: 'inventories.status', label: 'Estatus' },
 *   TENANT: false,
 * })
 */
export function buildCommonFilters(datasetKey, overrides = {}) {
  const result = []

  for (const [constantKey, defaults] of Object.entries(COMMON_DEFINITIONS)) {
    const override = overrides[constantKey]

    // Explicitly excluded
    if (override === false) continue

    const merged = { ...defaults, ...override }

    // Skip if no column defined (e.g. STATUS without override)
    if (!merged.column) continue

    // Usuario filter is a special case when resolving against the default users
    // table. Overrides that point to a different column (e.g. users_rh.soeid in
    // brigade_assignments) keep the plain embedFilter path.
    if (constantKey === 'USER' && merged.column === 'users.user') {
      result.push(userFilter(datasetKey, merged.label))
      continue
    }

    if (constantKey === 'PROFILE' && merged.column === 'users.id_profile') {
      // Profile filter is tied to the existence of a users table join,
      // which is usually indicated by the presence of a USER filter.
      if (overrides.USER === false) continue

      // Use embed type so only profiles with actual data in the dataset
      // are shown. profileMapItem resolves numeric IDs to labels via
      // PROFILE_MAPPING, and cascading filters narrow options automatically.
      result.push(embedFilter(datasetKey, 'users.id_profile', merged.label, merged.key, {
        optionsOverrides: { mapItem: profileMapItem },
      }))
      continue
    }

    result.push(embedFilter(datasetKey, merged.column, merged.label, merged.key, override || {}))
  }

  return result
}
